<script lang='ts'>
  import { Activity, type Exercise } from "@/Common/ActivityFunctions";
  import { Button } from "@/lib/components/ui/button";
  import ConfirmationPopover from "../Today/ConfirmationPopover.svelte";
  import { onDestroy, onMount } from "svelte";
  import * as ContextMenu from "$lib/components/ui/context-menu/index.js";
  import type { CalendarLoader } from "./CalendarLoaderNew";
  import * as Popover from '$lib/components/ui/popover/index.js';
  import { Label } from "@/lib/components/ui/label";
  import { Input } from "@/lib/components/ui/input";
  import { copyCalendarEvent, moveCalendarEvent, swapCalendarEvents } from "./CalendarEventUtils";
  import { toDateInputString, toTimeInputString } from "@/Util/utils";
  import { CreateCustomEventDispatchHandler } from "@/EventDispatchers";
  import { Separator } from "@/lib/components/ui/separator";
  import { today } from "@/time";

  export let activity: Exercise;
  export let disabled: boolean = false;
  export let calendarLoader: CalendarLoader;
  export let rootDiv: HTMLElement; // Used for dispatching custom events
  export let open;

  // @ts-ignore
  const jq = window.jQuery;
  //@ts-ignore
  const Xert = window.Xert;

  let moveActivityPopoverOpen = false;
  let copyActivityPopoverOpen = false;
  let swapActivityPopoverOpen = false;
  let inputDate: string;
  let inputTime: string;
  $: if (!inputDate) {
    inputDate = toDateInputString(new Date(activity.start_date));
    inputTime = toTimeInputString(new Date(activity.start_date));
  }

  $: isFlagged = Activity.isFlagged(activity);
  $: isPinned = Activity.isPinned(activity);
  $: isPlanned = Activity.isPlanned(activity);
  $: isStarred = Activity.isStarred(activity);

  $: isForecast = Activity.isForecast(activity);
  $: isForecastEvent = Activity.isForecastEvent(activity);
  $: isPlaceholder = Activity.isPlaceholder(activity);
  $: isRecorded = Activity.isRecorded(activity);
  $: noActivity = Activity.noActivity(activity);
  $: isGoalEvent = Activity.isGoalEvent(activity);

  $: canEdit = Activity.canEdit(activity);
  $: canMove = Activity.canMove(activity);
  $: canCopy = Activity.canCopy(activity);

  function flagActivity() {
    Activity.toggleFlag(activity);
    calendarLoader.updateActivity(activity, (a) => ({ ...a, flag: !isFlagged }));
  }

  function pinActivity() {
    Activity.togglePin(activity);
    calendarLoader.updateActivity(activity, (a) => ({ ...a, pinned: !isPinned }));
  }

  function favouriteActivity() {
    Activity.toggleFavorite(activity);
    calendarLoader.updateActivity(activity, (a) => ({ ...a, favorite: !isStarred }));
  }

  function deleteActivity() {
    Activity.clearFromCalendar(activity);
    calendarLoader.removeActivity(activity);
  }

  // TODO (hyde): don't do this to open the edit modal, this is bad old code
  let eventDispatchHandler;
  onMount(()=>{
    //Returns custom Event Dispatchers that can be used directly as event handlers
    //e.g. on:click={eventDispatcherHandler('myEvent', {...myEventDetails}, bubbles)}

    //Dispatches Custom Events that can be used within component methods
    //e.g. function myMethod() {/*modify component before call*/...; dispatchTrainingEvent('myEvent', {...myEventDetails}, bubbles); /*modify component after call*/  } 
    [eventDispatchHandler] = CreateCustomEventDispatchHandler(rootDiv);
  })

  onMount(() => {
    jq('[data-toggle=tooltip]').tooltip({container: '#shadcn-ui-dialog-target'});
  });

  // The tooltips live outside the menu. When the menu closes under the pointer (e.g. after confirming Clear) the
  // element is removed without a mouseleave, so its tooltip would stay on screen.
  onDestroy(() => {
    jq('#shadcn-ui-dialog-target > .tooltip').remove();
  });

</script>

{#if Activity.isRecorded(activity)}
  <ContextMenu.Item on:click={flagActivity} {disabled}>
    <i class={`${isFlagged ? 'fa-solid' : 'fa-regular'} fa-flag w-[20px]`} /> {isFlagged ? 'Unflag' : 'Flag'}
  </ContextMenu.Item>
{/if}
{#if isForecastEvent && !isPlaceholder}
  <ContextMenu.Item on:click={pinActivity} {disabled}>
    <i class={`${isPinned ? 'fa-solid' : 'fa-regular'} fa-thumbtack w-[20px]`} /> {isPinned ? 'Unpin' : 'Pin'}
  </ContextMenu.Item>
{/if}
{#if !isPlanned}
  <ContextMenu.Item on:click={favouriteActivity} {disabled}>
    <i class={`${isStarred ? 'fa-solid' : 'fa-regular'} fa-star w-[20px]`} /> {isStarred ? 'Unfavourite' : 'Favourite'}
  </ContextMenu.Item>
{/if}
{#if canEdit}
  <ContextMenu.Item {disabled} on:click={() => {
    open = false;
    eventDispatchHandler("editEvent", {date: new Date(activity.start_date), exercise: activity})();
  }}>
    <i class='fa-regular fa-pen-to-square w-[20px]' /> {isForecastEvent ? 'Choose Training' : 'Edit'}
  </ContextMenu.Item>
{:else if !isGoalEvent}
  <!-- Don't show greyed out button for goal events -->
  <div data-toggle='tooltip' title='Cannot edit activities recorded with power data' class='w-full'>
    <ContextMenu.Item disabled>
      <i class='fa-regular fa-pen-to-square w-[20px]' /> {isForecastEvent ? 'Choose Training' : 'Edit'}
    </ContextMenu.Item>
  </div>
{/if}
{#if isPlaceholder && !noActivity && !isGoalEvent}
  <ContextMenu.Item on:click={() => {
    open = false;
    eventDispatchHandler("editEvent", {date: new Date(activity.start_date), exercise: activity, openTo: "magic"})();
  }}>
    <i class='fa-regular fa-sparkles w-[20px]' /> Generate Workout
  </ContextMenu.Item>
{/if}

{#if isForecastEvent && !isGoalEvent}
  <Popover.Root bind:open={swapActivityPopoverOpen} portal="#shadcn-ui-dialog-target">
    <Popover.Trigger asChild let:builder>
      <Button {disabled} builders={[builder]} variant='ghost' size='sm' class='w-full justify-start text-sm px-2 py-1.5 font-normal cursor-auto' on:click={(e) => e.stopPropagation()} >
        <i class='fa-regular fa-swap w-[20px] text-start' /> Swap
      </Button>
    </Popover.Trigger>
    <Popover.Content side='top' class="flex flex-col gap-4">
      <div class="flex flex-row justify-between">
        <div class="space-y-1">
          <Label for="selected-date">Date</Label>
          <Input type="date" id="selected-date" bind:value={inputDate} min={$today.toISOString().split('T')[0]}/>
        </div>
        <div class="space-y-1">
          <Label for="selected-time">Time</Label>
          <Input type="time" id="selected-time" bind:value={inputTime} />
        </div>
      </div>
      <div class="flex justify-end gap-2">
        <Button size="sm" variant="outline" on:click={() => {swapActivityPopoverOpen = false}}>
          Cancel
        </Button>
        <Button size="sm" variant="default" on:click={() => {
          const date = new Date(`${inputDate}T${inputTime}`);
          const targetActivity = calendarLoader.getActivitiesForDate(date).find((la) => Activity.isForecastEvent(la.activity))?.activity;
          if (targetActivity) {
            swapCalendarEvents(activity, targetActivity, () => calendarLoader.swapActivities(activity, targetActivity), Xert);
          } else {
            Xert?.warning('<strong>Warning:</strong> No forecast activity found for the selected date');
          }
          swapActivityPopoverOpen = false;
        }}>
          Swap
        </Button>
      </div>
    </Popover.Content>
  </Popover.Root>
{/if}

{#if canCopy}
  <Popover.Root bind:open={copyActivityPopoverOpen} portal="#shadcn-ui-dialog-target">
    <Popover.Trigger asChild let:builder>
      <Button {disabled} builders={[builder]} variant='ghost' size='sm' class='w-full justify-start text-sm px-2 py-1.5 font-normal cursor-auto' on:click={(e) => e.stopPropagation()} >
        <i class='fa-regular fa-copy w-[20px] text-start' /> Copy
      </Button>
    </Popover.Trigger>
    <Popover.Content side='top' class="flex flex-col gap-4">
      <div class="flex flex-row justify-between">
        <div class="space-y-1">
          <Label for="selected-date">Date</Label>
          <Input type="date" id="selected-date" bind:value={inputDate} />
        </div>
        <div class="space-y-1">
          <Label for="selected-time">Time</Label>
          <Input type="time" id="selected-time" bind:value={inputTime} />
        </div>
      </div>
      <div class="flex justify-end gap-2">
        <Button size="sm" variant="outline" on:click={() => {copyActivityPopoverOpen = false}}>
          Cancel
        </Button>
        <Button size="sm" variant="default" on:click={() => {
          const date = new Date(`${inputDate}T${inputTime}`);
          copyCalendarEvent(activity, date, () => calendarLoader.copyActivity(activity, date), Xert)
          copyActivityPopoverOpen = false
        }}>
          Copy
        </Button>
      </div>
    </Popover.Content>
  </Popover.Root>
{:else}
  <div data-toggle='tooltip' title={isForecastEvent ? 'Cannot copy forecast activities' : 'Cannot copy this activity'} class='w-full'>
    <Button disabled variant='ghost' size='sm' class='w-full justify-start text-sm px-2 py-1.5 font-normal cursor-auto'>
      <i class='fa-regular fa-copy w-[20px] text-start' /> Copy
    </Button>
  </div>
{/if}

{#if canMove}
  <Popover.Root bind:open={moveActivityPopoverOpen} portal="#shadcn-ui-dialog-target">
    <Popover.Trigger asChild let:builder>
      <Button {disabled} builders={[builder]} variant='ghost' size='sm' class='w-full justify-start text-sm px-2 py-1.5 font-normal cursor-auto' on:click={(e) => e.stopPropagation()} >
        <i class='fa-regular fa-calendar-day w-[20px] text-start' /> Move
      </Button>
    </Popover.Trigger>
    <Popover.Content side='top' class="flex flex-col gap-4">
      <div class="flex flex-row justify-between">
        <div class="space-y-1">
          <Label for="selected-date">Date</Label>
          <Input type="date" id="selected-date" bind:value={inputDate} min={isPlanned ? $today.toISOString().split('T')[0] : undefined} />
        </div>
        <div class="space-y-1">
          <Label for="selected-time">Time</Label>
          <Input type="time" id="selected-time" bind:value={inputTime} />
        </div>
      </div>
      <div class="flex justify-end gap-2">
        <Button size="sm" variant="outline" on:click={() => {moveActivityPopoverOpen = false}}>
          Cancel
        </Button>
        <Button size="sm" variant="default" on:click={() => {
          const date = new Date(`${inputDate}T${inputTime}`);
          moveCalendarEvent(activity, date, () => calendarLoader.moveActivity(activity, date), Xert)
          moveActivityPopoverOpen = false
        }}>
          Move
        </Button>
      </div>
    </Popover.Content>
  </Popover.Root>
{:else}
  <div data-toggle='tooltip' title={isForecastEvent ? 'Cannot move forecast activities' : (isRecorded ? 'Cannot move recorded activities' : 'Cannot move this activity')} class='w-full'>
    <Button disabled variant='ghost' size='sm' class='w-full justify-start text-sm px-2 py-1.5 font-normal cursor-auto'>
      <i class='fa-regular fa-calendar-day w-[20px] text-start' /> Move
    </Button>
  </div>
{/if}

{#if !isPlaceholder}
  <Separator class="bg-muted -mx-1 my-1 h-px w-full" />
  <ConfirmationPopover title="{isForecastEvent ? 'Clear' : 'Delete'} this activity?" onConfirm={deleteActivity} let:builder>
    <Button {disabled} builders={[builder]} variant='ghost' size='sm' class='w-full justify-start text-sm px-2 py-1.5 font-normal cursor-auto' on:click={(e) => e.stopPropagation()} >
      <i class='fa-regular {isForecastEvent ? 'fa-xmark-large' : 'fa-trash'} w-[20px] text-start' /> {isForecastEvent ? 'Clear' : 'Delete'}
    </Button>
  </ConfirmationPopover>
{/if}

<!-- TODO (hyde): remove this button -->
<!-- <Button variant='ghost' size='sm' class='w-full justify-start text-sm px-2 py-1.5 font-normal cursor-auto' on:click={(e) => {e.stopPropagation(); console.log(activity);}} >
  <i class='fa-regular fa-dragon w-[20px] text-start' /> Console.log
</Button> -->
